import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { OnboardingTour } from './OnboardingTour';
import { TrialBanner } from './TrialBanner';
import axios from 'axios';
import {
  Building2,
  LayoutDashboard,
  Plus,
  List,
  Users,
  LogOut,
  Menu,
  X,
  FileText,
  Map,
  MapPinned,
  Settings,
  Settings2,
  Wallet,
  Store,
  ShoppingBag,
  Sun,
  Moon,
  User,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Printer,
  Shield,
  Crown,
  BarChart3,
  LifeBuoy,
  Bell,
  Palette,
  Activity,
  ClipboardList,
  LayoutTemplate,
  Sparkles,
  Globe,
  Clock,
  ClipboardCheck
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BiometricPrompt } from './BiometricPrompt';
import { usePushNotifications } from '../hooks/usePushNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { t, language, setLanguage, languages } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [mapsMenuOpen, setMapsMenuOpen] = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [newMarketplaceCount, setNewMarketplaceCount] = useState(0);
  const [newWalletTxCount, setNewWalletTxCount] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const { isSubscribed, isSupported, subscribe: subscribePush } = usePushNotifications();
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const isStrictAdmin = user?.role === 'admin';

  // Check if current path is in maps submenu
  const isInMapsMenu = ['/town-planning', '/guide-maps', '/detailed-print'].includes(location.pathname);

  // Auto-expand maps menu if on a maps page
  useEffect(() => {
    if (isInMapsMenu) {
      setMapsMenuOpen(true);
    }
  }, [isInMapsMenu]);

  // Show push notification prompt once
  useEffect(() => {
    if (isSupported && !isSubscribed && !localStorage.getItem('push_prompt_dismissed')) {
      const timer = setTimeout(() => setShowPushPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [isSupported, isSubscribed]);

  // Mark marketplace as visited when user navigates to marketplace
  useEffect(() => {
    const markMarketplaceVisited = async () => {
      if (location.pathname === '/marketplace-view') {
        try {
          await axios.post(`${API}/marketplace/mark-visited`);
          setNewMarketplaceCount(0);
        } catch (err) {
          console.error('Failed to mark marketplace visited:', err);
        }
      }
    };
    markMarketplaceVisited();
  }, [location.pathname]);

  // Mark wallet as visited when user navigates to wallet
  useEffect(() => {
    const markWalletVisited = async () => {
      if (location.pathname === '/wallet') {
        try {
          await axios.post(`${API}/wallet/mark-visited`);
          setNewWalletTxCount(0);
        } catch (err) {
          console.error('Failed to mark wallet visited:', err);
        }
      }
    };
    markWalletVisited();
  }, [location.pathname]);

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/wallet`);
        setWalletBalance(res.data.balance || 0);
      } catch (error) {
        if (error.response?.status === 401) {
          console.log('Token might be expired');
        }
      }
    };
    const fetchOpenTickets = async () => {
      if (!isAdmin) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/support/tickets`);
        const open = res.data.filter(t => t.status === 'open' || t.status === 'in_progress').length;
        setOpenTicketCount(open);
      } catch {}
    };
    const fetchUnreadNotifs = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/notifications/unread-count`);
        setUnreadNotifCount(res.data.count || 0);
      } catch {}
    };
    const fetchNewMarketplaceCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/marketplace/new-count`);
        setNewMarketplaceCount(res.data.new_count || 0);
      } catch {}
    };
    const fetchNewWalletTxCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/wallet/new-transactions-count`);
        setNewWalletTxCount(res.data.new_count || 0);
      } catch {}
    };
    const fetchSubscriptionStatus = async () => {
      if (isAdmin) return; // Admins don't have subscriptions
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/subscription/expiry-status`);
        setSubscriptionStatus(res.data);
      } catch {}
    };
    fetchWallet();
    fetchOpenTickets();
    fetchUnreadNotifs();
    fetchNewMarketplaceCount();
    fetchNewWalletTxCount();
    fetchSubscriptionStatus();

    // Check tour status on first render
    const checkTour = async () => {
      try {
        const res = await axios.get(`${API}/users/me/tour-status`);
        if (!res.data.tour_completed) setShowTour(true);
      } catch {}
    };
    checkTour();
  }, [location.pathname, isAdmin]);

  const navItems = [
    { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, admin: false },
    { path: '/properties/add', labelKey: 'nav.addProperty', icon: Plus, admin: false },
    { path: '/properties', labelKey: 'nav.properties', icon: List, admin: false },
    { path: '/sold-properties', labelKey: 'nav.soldCRM', icon: Users, admin: false },
    { path: '/client-requirements', labelKey: 'nav.clientRequirements', icon: ClipboardCheck, admin: false },
    { path: '/marketplace-view', labelKey: 'nav.marketplace', icon: Store, admin: false },
    { path: '/map-search', labelKey: 'nav.exploreMap', icon: MapPinned, admin: false },
    { path: '/my-listings', labelKey: 'nav.myListingsAnalytics', icon: ShoppingBag, admin: false },
    // Maps dropdown is inserted here in the render logic
    { path: '/chat-with-ai', labelKey: 'nav.chatWithAI', icon: MessageSquare, admin: false },
    { path: '/notifications', labelKey: 'nav.notifications', icon: Bell, admin: false },
    { path: '/settings', labelKey: 'nav.settings', icon: Settings, admin: false },
    { path: '/support', labelKey: 'nav.support', icon: LifeBuoy, admin: false },
    { path: '/wallet', labelKey: 'nav.wallet', icon: Wallet, admin: false },
    { path: '/subscription', labelKey: 'nav.subscription', icon: Crown, admin: false },
    { path: '/admin-panel', labelKey: 'nav.adminPanel', icon: Shield, admin: true },
    { path: '/agent-analytics', labelKey: 'Agent Analytics', icon: BarChart3, admin: true },
    { path: '/admin-presets', labelKey: 'Theme Presets', icon: Palette, admin: true },
    { path: '/form-builder', labelKey: 'nav.formBuilder', icon: Settings2, admin: true },
    { path: '/health', labelKey: 'System Health', icon: Activity, admin: true },
    { path: '/audit-logs', labelKey: 'Audit Logs', icon: ClipboardList, admin: true },
    { path: '/share-templates', labelKey: 'Share Templates', icon: LayoutTemplate, admin: true },
    { path: '/trial-management', labelKey: 'Trial Management', icon: Clock, admin: true },
    { path: '/push-notifications', labelKey: 'Push Notifications', icon: Bell, admin: true },
  ];

  const mapsSubItems = [
    { path: '/town-planning', labelKey: 'nav.townPlanning', icon: FileText },
    { path: '/guide-maps', labelKey: 'nav.guideMaps', icon: Map },
    { path: '/detailed-print', labelKey: 'nav.detailedPrint', icon: Printer },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleTourComplete = async () => {
    setShowTour(false);
    try { await axios.post(`${API}/users/me/complete-tour`); } catch {}
  };

  const handleReplayTour = async () => {
    try { await axios.post(`${API}/users/me/reset-tour`); } catch {}
    setShowTour(true);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#212A31]' : 'bg-[#EDE8F5]'}`}>
      {/* Mobile Menu Button - only show when sidebar is closed */}
      {!sidebarOpen && (
        <div
          className="lg:hidden fixed left-4 z-50"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
        >
          <Button
            onClick={() => setSidebarOpen(true)}
            className={`${isDark ? 'bg-[#2E3944] border-white/10 hover:bg-[#3a4a54] text-white' : 'bg-white border-gray-200 hover:bg-gray-100 text-gray-800'} border`}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      )}

      {/* Sidebar */}
      <AnimatePresence>
        {(sidebarOpen || window.innerWidth >= 1024) && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ duration: 0.3 }}
            className={`fixed left-0 top-0 h-screen w-64 ${isDark ? 'bg-[#2E3944] border-white/5' : 'bg-white border-[#ADBBDA]'} border-r z-40 lg:translate-x-0`}
            style={{ overflow: 'hidden' }}
            data-testid="sidebar"
          >
            <div className="h-full flex flex-col" style={{ maxHeight: '100vh', overflow: 'hidden' }}>
            <div
              className={`flex-shrink-0 p-6 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}
              style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)' }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h1 className={`text-xl font-serif font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Property<span className="text-[var(--brand)]">Shop</span>
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{user?.role === 'admin' ? t('common.admin') : user?.role === 'manager' ? 'Manager' : t('common.agent')} {t('common.portal')}</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Theme Toggle */}
                  <button
                    onClick={toggleTheme}
                    data-testid="theme-toggle"
                    className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-[#3a4a54] hover:bg-[#4a5a64] text-yellow-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    title={isDark ? t('settings.lightMode') : t('settings.darkMode')}
                  >
                    {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </button>
                  {/* Mobile Close Button */}
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className={`lg:hidden p-2 rounded-lg transition-colors ${isDark ? 'bg-[#3a4a54] hover:bg-[#4a5a64] text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    data-testid="sidebar-close-button"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Wallet Balance Card */}
            <div className={`flex-shrink-0 p-4 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
              <Link to="/wallet" className="block">
                <div className={`p-3 bg-gradient-to-r ${isDark ? 'from-[var(--brand)]/20' : 'from-[var(--brand)]/10'} to-transparent rounded-lg hover:from-[var(--brand)]/30 transition-colors`}>
                  <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-[var(--brand)]" />
                    <div>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('common.balance')}</p>
                      <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="sidebar-balance">
                        {walletBalance} <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('common.points')}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
              {navItems.map((item, index) => {
                if (item.admin && !isAdmin) return null;
                if (item.agentOnly && isAdmin) return null;
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                const label = item.labelKey.includes('.') ? t(item.labelKey) : item.labelKey;
                
                // Insert Maps dropdown after My Listings & Analytics
                const showMapsDropdown = item.path === '/my-listings';
                
                return (
                  <React.Fragment key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.labelKey.includes('.') ? item.labelKey.split('.')[1] : item.labelKey}`}
                      className={`sidebar-link flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm ${
                        isActive ? 'active text-[var(--brand)]' : isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{label}</span>
                      {/* Marketplace new listings badge */}
                      {item.path === '/marketplace-view' && newMarketplaceCount > 0 && (
                        <span
                          className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-500 text-[10px] font-bold"
                          data-testid="marketplace-new-badge"
                        >
                          <Bell className="w-3 h-3" />
                          {newMarketplaceCount} new
                        </span>
                      )}
                      {/* Wallet new transactions badge */}
                      {item.path === '/wallet' && newWalletTxCount > 0 && (
                        <span
                          className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-500 text-[10px] font-bold"
                          data-testid="wallet-new-badge"
                        >
                          <Bell className="w-3 h-3" />
                          {newWalletTxCount} new
                        </span>
                      )}
                      {/* Subscription expiry warning badge */}
                      {item.path === '/subscription' && subscriptionStatus?.show_warning && (
                        <span
                          className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-500 text-[10px] font-bold animate-pulse"
                          data-testid="subscription-warning-badge"
                        >
                          <Bell className="w-3 h-3" />
                          {subscriptionStatus.days_remaining}d left
                        </span>
                      )}
                      {/* Subscription expired badge */}
                      {item.path === '/subscription' && subscriptionStatus?.show_expired && (
                        <span
                          className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-500 text-[10px] font-bold animate-pulse"
                          data-testid="subscription-expired-badge"
                        >
                          <Bell className="w-3 h-3" />
                          Expired
                        </span>
                      )}
                      {item.path === '/notifications' && unreadNotifCount > 0 && (
                        <span
                          className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold"
                          data-testid="notification-badge"
                        >
                          {unreadNotifCount}
                        </span>
                      )}
                      {item.path === '/support' && isAdmin && openTicketCount > 0 && (
                        <span
                          className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold"
                          data-testid="support-badge"
                        >
                          {openTicketCount}
                        </span>
                      )}
                    </Link>
                    
                    {/* Maps Dropdown - shown after My Listings & Analytics */}
                    {showMapsDropdown && (
                      <div className="mt-1">
                        <button
                          onClick={() => setMapsMenuOpen(!mapsMenuOpen)}
                          data-testid="nav-maps-dropdown"
                          className={`sidebar-link flex items-center justify-between w-full px-4 py-2.5 rounded-lg text-sm ${
                            isInMapsMenu ? 'active text-[var(--brand)]' : isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Map className="w-5 h-5" />
                            <span>{t('nav.maps')}</span>
                          </div>
                          {mapsMenuOpen ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                        
                        <AnimatePresence>
                          {mapsMenuOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="ml-4 mt-1 space-y-1 border-l-2 border-[var(--brand)]/20 pl-2">
                                {mapsSubItems.map((subItem) => {
                                  const SubIcon = subItem.icon;
                                  const isSubActive = location.pathname === subItem.path;
                                  return (
                                    <Link
                                      key={subItem.path}
                                      to={subItem.path}
                                      data-testid={`nav-${subItem.labelKey.split('.')[1]}`}
                                      className={`sidebar-link flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                                        isSubActive ? 'active text-[var(--brand)]' : isDark ? 'text-gray-400' : 'text-gray-600'
                                      }`}
                                      onClick={() => setSidebarOpen(false)}
                                    >
                                      <SubIcon className="w-4 h-4" />
                                      <span>{t(subItem.labelKey)}</span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </nav>

            <div className={`flex-shrink-0 p-4 border-t ${isDark ? 'border-white/5 bg-[#2E3944]' : 'border-gray-200 bg-white'}`}>
              <div className={`mb-4 p-3 ${isDark ? 'bg-[#212A31]' : 'bg-gray-100'} rounded-lg`}>
                <div className="flex items-center gap-3">
                  {/* Profile Photo */}
                  <div className={`w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ${isDark ? 'bg-[#3a4a54]' : 'bg-gray-200'} flex items-center justify-center`}>
                    {user?.profile_photo ? (
                      <img
                        src={`data:image/jpeg;base64,${user.profile_photo}`}
                        alt={user?.name}
                        className="w-full h-full object-cover"
                        data-testid="sidebar-profile-photo"
                      />
                    ) : (
                      <User className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('common.signedInAs')}</p>
                    <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'} font-medium truncate`}>{user?.name}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate`}>{user?.email}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    const idx = languages.findIndex(l => l.code === language);
                    const next = languages[(idx + 1) % languages.length];
                    setLanguage(next.code);
                  }}
                  data-testid="sidebar-language-toggle"
                  className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors border ${
                    isDark
                      ? 'border-white/10 text-gray-300 hover:text-white hover:bg-white/5'
                      : 'border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  title={t('nav.language') || 'Change language'}
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>
                    {languages.find(l => l.code === language)?.flag}{' '}
                    {languages.find(l => l.code === language)?.nativeName || language.toUpperCase()}
                  </span>
                </button>
                <button
                  onClick={handleReplayTour}
                  data-testid="replay-tour-button"
                  className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Replay Tour
                </button>
                <Button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="w-full bg-transparent border border-[var(--brand)]/30 text-[var(--brand)] hover:bg-[var(--brand)]/10"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </Button>
              </div>
            </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <TrialBanner />

        {/* Push Notification Opt-in Banner */}
        {showPushPrompt && (
          <div className={`mx-4 md:mx-8 mt-16 md:mt-2 mb-2 p-3 rounded-lg flex items-center justify-between gap-3 ${isDark ? 'bg-[var(--brand)]/10 border border-[var(--brand)]/20' : 'bg-amber-50 border border-amber-200'}`} data-testid="push-prompt-banner">
            <div className="flex items-center gap-2 min-w-0">
              <Bell className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[var(--brand)]' : 'text-amber-600'}`} />
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enable push notifications to stay updated</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={async () => { await subscribePush(); setShowPushPrompt(false); }}
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[var(--brand)] text-white hover:opacity-90 transition-opacity"
                data-testid="push-enable-btn"
              >
                Enable
              </button>
              <button
                onClick={() => { setShowPushPrompt(false); localStorage.setItem('push_prompt_dismissed', 'true'); }}
                className={`px-2 py-1.5 rounded-md text-xs ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="push-dismiss-btn"
              >
                Later
              </button>
            </div>
          </div>
        )}

        <div
          className="px-4 pb-4 pt-16 md:px-8 md:pb-8 lg:pt-8"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4rem)' }}
        >
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Biometric Login Prompt for mobile users */}
      <BiometricPrompt />

      {/* Onboarding Tour */}
      <OnboardingTour isOpen={showTour} onComplete={handleTourComplete} isDark={isDark} />
    </div>
  );
};